/* ============================================================
   PHREE SKIN — the BALANCE dot-grid (scroll-scrubbed).
   ONE idea in the brand's Braille-dot language: your skin's microbiome as a
   field of dots. DISRUPTED = scattered off the grid, patchy (gaps), a few warm
   reactive dots, dim. Scroll and the field does ONE thing — it settles onto the
   grid: every dot present, cool, even, the bottom rows filling into a sealed
   barrier. No lines (pure dots). No invented numbers. One scalar `heal` ∈ [0,1].

   SCRUB MODE (data-mb-scrub): heal is tied DIRECTLY to scroll progress through a
   pinned track (.mbz). The field is frozen when you stop; it only moves as you
   scroll. Three science beats cross-fade with progress (problem → mechanism →
   result). No toggle, no reset — Phree is a scroll interaction.

   Mount:  <canvas data-balance data-mb-scrub data-gap="34" data-dot="3.4" id="…">
   Host [data-mb-host] may carry data-mb-scroll / data-mb-scrub / data-mb-start.
   Reflects state via `.is-healed` on host + stage + any [data-mb-toggle] button.
   Single rAF, DPR-aware, IO-gated; reduced-motion = one static balanced frame.
   ============================================================ */
(function () {
  "use strict";
  var reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
  var DPR = Math.min(2, window.devicePixelRatio || 1);
  var TAU = 6.28318530718;
  var SKY = [115, 179, 206], DEEP = [78, 150, 180], CORAL = [219, 138, 120];

  function lerp(a, b, t) { return a + (b - a) * t; }
  function clamp01(x) { return x < 0 ? 0 : x > 1 ? 1 : x; }
  function bez(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
  function eo3(x) { return 1 - Math.pow(1 - x, 3); }
  function smooth(a, b, x) { x = clamp01((x - a) / (b - a)); return x * x * (3 - 2 * x); }

  function mountAll() {
    document.querySelectorAll("canvas[data-balance]").forEach(function (cv) {
      if (cv.__balMounted) return; cv.__balMounted = true; new Engine(cv);
    });
  }

  function Engine(cv) {
    var self = this;
    this.cv = cv; this.ctx = cv.getContext("2d");
    this.host = cv.closest("[data-mb-host]") || cv.parentElement;
    this.stage = (this.host.querySelector && this.host.querySelector(".mbstage")) || this.host;
    this.scrub = this.host.hasAttribute("data-mb-scrub") || cv.hasAttribute("data-mb-scrub");
    this.track = cv.closest(".mbz") || this.host;
    this.scrollDriven = this.scrub || this.host.hasAttribute("data-mb-scroll") || cv.hasAttribute("data-mb-scroll");
    /* AUTOPLAY (data-mb-auto). Scrub mode ties the whole story to scroll position, so a visitor who
       stops moving sees one frozen frame and never learns there were three beats. With this on, the
       story plays itself on its own clock as soon as the section is in view — and the moment the
       visitor actually scrubs, scroll takes over for good (autoDone). Nobody loses content by
       sitting still, and nobody fights the animation for scrolling. */
    this.auto = this.host.hasAttribute("data-mb-auto") || cv.hasAttribute("data-mb-auto");
    this.autoT0 = null;
    this.autoDone = false;
    /* armed = the panel has been properly in view at least once this visit, so the clock may run.
       autoHold = on screen but not yet properly in view: keep drawing, hold the story. */
    this.armed = false;
    this.autoHold = true;
    this.autoHeld = false;   /* latched once the heal has run to completion */
    var s = this.host.getAttribute("data-mb-start") || cv.getAttribute("data-mb-start");
    this.heal = s != null ? +s : 0;
    this.target = this.heal; this.tStart = this.heal; this.t0e = 0; this.dur = 1300;
    this.gap = +cv.getAttribute("data-gap") || 30;
    this.dotR = +cv.getAttribute("data-dot") || 3.2;
    this.running = false; this.raf = 0; this.now = (performance && performance.now) ? performance.now() : Date.now();
    /* beats + progress dots (scrub storytelling) */
    this.beats = [].slice.call((this.host.querySelectorAll && this.host.querySelectorAll("[data-beat]")) || []);
    this.pdots = [].slice.call((this.host.querySelectorAll && this.host.querySelectorAll(".mbz__pdot")) || []);
    this.beatIdx = -1;

    this.btns = [];
    (this.host.querySelectorAll ? this.host.querySelectorAll("[data-mb-toggle]") : []).forEach(function (b) {
      self.btns.push(b); b.addEventListener("click", function () { self.toggle(); });
    });

    this.resize = this.resize.bind(this); this.step = this.step.bind(this);
    var deb; window.addEventListener("resize", function () { clearTimeout(deb); deb = setTimeout(self.resize, 160); });
    if (this.scrollDriven && !reduce) window.addEventListener("scroll", function () { self.onScroll(); }, { passive: true });

    this.resize();
    /* WHEN THE CLOCK STARTS IS THE WHOLE EFFECT.
       This used to observe at threshold 0, so the 9s heal began the instant the top edge of a
       720px section crossed the bottom of the viewport — a full screen-height before the panel
       was actually in front of anyone. By the time you had scrolled to it the run was over and
       the swarm sat frozen in its healed state, which reads exactly like a scroll-triggered
       animation that failed to trigger. Reported as "the dots all stay the same", and measured:
       the section already carried `is-healed` on arrival.

       So: keep painting from the moment any part of it is on screen (arriving mid-animation is
       better than arriving to a blank canvas), but hold the clock until the panel is genuinely
       being looked at. ENTER is deliberately forgiving on short viewports, where a 720px section
       can never reach 55% of a phone screen. */
    var ENTER = 0.55;
    new IntersectionObserver(function (es) {
      es.forEach(function (e) {
        if (!e.isIntersecting) { self.armed = false; self.stop(); return; }
        var enough = e.intersectionRatio >= ENTER ||
                     e.intersectionRect.height >= (window.innerHeight || 0) * 0.6;
        if (self.auto && !self.autoDone) {
          if (enough) {
            /* Arm on a real entry. Once the heal has completed (autoHeld) the clock is
               never reset again — scrolling back up to a sealed barrier and watching it
               shatter so it can re-heal is the reversal bug by another route. */
            if (!self.armed && !self.autoHeld) { self.autoT0 = null; }
            self.armed = true;
          } else if (!self.armed && !self.autoHeld) {
            self.autoT0 = null;
          }
          self.autoHold = !self.armed;
        }
        reduce ? self.renderStatic() : self.start();
      });
    }, { threshold: [0, 0.15, 0.3, ENTER, 0.75, 1] }).observe(this.track);
    /* a backgrounded tab throttles rAF; restart the clock on return so the story is not half-played */
    document.addEventListener("visibilitychange", function () {
      if (document.visibilityState === "visible" && self.auto && !self.autoDone) {
        self.autoT0 = null;
        if (!reduce && self.visible()) self.start();
      }
    });
    this.sync();
    window.addEventListener("load", function () { if (self.scrollDriven && !reduce) self.onScroll(); if (reduce) self.renderStatic(); });
    if (this.scrollDriven && !reduce) this.onScroll();
    if (reduce) { this.setBeat(2); this.renderStatic(); }
  }

  Engine.prototype.resize = function () {
    var r = this.cv.getBoundingClientRect();
    this.W = Math.max(1, r.width); this.H = Math.max(1, r.height);
    this.cv.width = Math.round(this.W * DPR); this.cv.height = Math.round(this.H * DPR);
    this.cv.style.width = this.W + "px"; this.cv.style.height = this.H + "px";
    this.build();
    if (reduce) this.renderStatic();
  };

  Engine.prototype.build = function () {
    var gap = this.gap, W = this.W, H = this.H;
    var cols = Math.max(4, Math.floor((W - gap * 0.6) / gap));
    var rows = Math.max(4, Math.floor((H - gap * 0.6) / gap));
    var offx = (W - (cols - 1) * gap) / 2, offy = (H - (rows - 1) * gap) / 2;
    this.rows = rows; this.cols = cols; this.nodes = [];
    for (var iy = 0; iy < rows; iy++) for (var ix = 0; ix < cols; ix++) {
      var ang = Math.random() * TAU, mag = gap * (0.3 + Math.random() * 0.6);
      var barrier = iy >= rows - 2;
      var weak = barrier ? (Math.random() < 0.72) : (Math.random() < 0.3);
      var warm = !barrier && Math.random() < 0.2;
      this.nodes.push({
        gx: offx + ix * gap, gy: offy + iy * gap,
        ox: Math.cos(ang) * mag, oy: Math.sin(ang) * mag,
        ph: Math.random() * TAU, sp: Math.random() < 0.5 ? SKY : DEEP,
        weak: weak, warm: warm, barrier: barrier, th: Math.random() * 0.55
      });
    }
  };

  Engine.prototype.toggle = function () {
    this.toggled = !this.toggled; this.ease(this.toggled ? 1 : 0, 1400); this.sync(this.toggled ? 1 : 0);
    if (reduce) { this.heal = this.target = this.toggled ? 1 : 0; this.renderStatic(); }
  };
  Engine.prototype.ease = function (to, dur) {
    this.tStart = this.heal; this.target = clamp01(to); this.t0e = this.now; this.dur = dur || 1300;
    if (!this.running && !reduce && this.visible()) this.start();
  };
  Engine.prototype.visible = function () { var r = this.cv.getBoundingClientRect(); return r.bottom > 0 && r.top < (window.innerHeight || 0); };

  Engine.prototype.onScroll = function () {
    if (this.scrub) {
      /* progress of the pinned track through the viewport → heal, DIRECTLY (no
         easing lag): the field is frozen unless you're scrolling. */
      var tr = this.track.getBoundingClientRect();
      var vh = window.innerHeight || 1;
      var total = this.track.offsetHeight - vh;
      var p = total > 0 ? clamp01(-tr.top / total) : (tr.top < vh * 0.5 ? 1 : 0);
      /* the visitor is scrubbing for real — hand control over and stop the autoplay competing */
      /* Scroll only takes over when this section is actually scroll-driven. The
         scrub was removed (it read as a scroll-jack), so without this guard any
         scroll past the section set autoDone and killed the autoplay. */
      if (this.scrollDriven && this.auto && !this.autoDone && p > 0.02) this.autoDone = true;
      /* entry-cue dismissal: once real progress into the track begins, drop the
         "keep scrolling" hint for good (one-way — never re-shown mid-scroll). */
      if (p > 0.02 && this.track.classList && !this.track.classList.contains("mbz--scrolled")) {
        this.track.classList.add("mbz--scrolled");
      }
      /* ease toward the scroll position (don't snap) — a forgiving, springy follow
         that settles when you stop scrolling. */
      this.ease(p, 520);
      this.setBeat(p < 0.34 ? 0 : p < 0.68 ? 1 : 2);
      this.sync(p);
      if (!this.running && this.visible()) this.start();
      return;
    }
    /* legacy scroll-drive: map the stage centre travelling up through the viewport */
    var r = this.stage.getBoundingClientRect(), vh2 = window.innerHeight || 1, c = r.top + r.height / 2;
    var q = clamp01((0.86 * vh2 - c) / (0.46 * vh2));
    this.ease(q, 440); this.toggled = q >= 0.5; this.sync(q);
  };

  Engine.prototype.setBeat = function (idx) {
    if (idx === this.beatIdx) return; this.beatIdx = idx;
    this.beats.forEach(function (b) { b.classList.toggle("is-on", +b.getAttribute("data-beat") === idx); });
    this.pdots.forEach(function (d, i) { d.classList.toggle("is-on", i === idx); });
  };

  Engine.prototype.sync = function (pending) {
    var healed = (pending != null ? pending : this.target) >= 0.5;
    this.btns.forEach(function (b) {
      b.classList.toggle("is-healed", healed); b.setAttribute("aria-pressed", healed ? "true" : "false");
      var on = b.querySelector("[data-mb-on]"), off = b.querySelector("[data-mb-off]");
      if (on) on.hidden = !healed; if (off) off.hidden = healed;
    });
    if (this.stage.classList) this.stage.classList.toggle("is-healed", healed);
    if (this.host.classList) this.host.classList.toggle("is-healed", healed);
  };
  Engine.prototype.tick = function () {
    if (reduce) { this.heal = this.target; return; }
    if (Math.abs(this.heal - this.target) < 0.0006) { this.heal = this.target; return; }
    var e = clamp01((this.now - this.t0e) / this.dur); this.heal = lerp(this.tStart, this.target, bez(e));
  };

  Engine.prototype.draw = function (tt) {
    var ctx = this.ctx, h = this.heal, seal = eo3(h), W = this.W;
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0); ctx.clearRect(0, 0, W, this.H);
    /* idle wobble ONLY in the non-scrub legacy mode; scrub stays frozen between scrolls.
       The healed end-state used to settle to exactly 0, so the swarm stopped dead the moment the
       story finished and the panel read as a still image for as long as you looked at it. A
       balanced microbiome is alive, not stopped — so amplitude falls away with the heal but never
       reaches zero. 0.7px is under a dot radius: motion you register, not motion you watch. */
    var breathe = (this.scrub || reduce) ? 0 : Math.max(0.7, (1 - h) * 3);
    var base = lerp(0.42, 0.95, h);
    for (var i = 0; i < this.nodes.length; i++) {
      var n = this.nodes[i];
      var x = n.gx + n.ox * (1 - seal) + (breathe ? Math.sin(tt / 950 + n.ph) * breathe : 0);
      var y = n.gy + n.oy * (1 - seal) + (breathe ? Math.cos(tt / 1050 + n.ph) * breathe : 0);
      var pres = n.weak ? smooth(n.th, Math.min(1, n.th + 0.35), h) : 1;
      var alpha = n.barrier ? pres * lerp(0.14, 1, seal) : pres * base;
      if (alpha <= 0.02) continue;
      var col = n.warm ? [lerp(CORAL[0], SKY[0], h), lerp(CORAL[1], SKY[1], h), lerp(CORAL[2], SKY[2], h)] : n.sp;
      var r = n.barrier ? this.dotR * lerp(0.75, 1.3, seal) : this.dotR * lerp(0.9, 1.02, h);
      ctx.fillStyle = "rgba(" + (col[0] | 0) + "," + (col[1] | 0) + "," + (col[2] | 0) + "," + alpha.toFixed(3) + ")";
      ctx.beginPath(); ctx.arc(x, y, r, 0, TAU); ctx.fill();
    }
  };

  /* Drive the SAME progress->beat mapping the scrub path uses, from a clock instead of scroll.
     One pass: 9s to heal, then held healed. It runs once per view rather than looping forever —
     a permanently cycling hero is a distraction next to a buy button, and the healed state is the
     one that sells. Re-entering the section replays it. */
  Engine.prototype.autoAdvance = function (now) {
    /* ONE WAY, AND IT LATCHES.
       The previous version looped: heal 6s, hold 3.2s, FALL 2.2s, rest, repeat.
       Measured stationary on the live theme, two seconds apart, the field's mean
       alpha ran 110 -> 153 -> 181 -> 181 -> 123 -> 73 and `is-healed` went
       true -> true -> false. The barrier sealed and then UNSEALED, on a loop, in
       front of the buy button. A visitor arriving mid-fall watches the product's
       central claim play backwards, and the chip beside it flips to "disrupted"
       under a headline that says balance returns.

       So it runs once, forward, and stays where it lands. 3.2s is inside the
       dwell window (the panel is on screen for roughly 1200px of scroll), and
       `autoHeld` keeps it healed if you scroll back up to it. */
    if (this.autoT0 == null) this.autoT0 = now;
    var RISE = 3200;
    var p = clamp01((now - this.autoT0) / RISE);
    this.target = p; this.tStart = p; this.heal = p; this.t0e = now; this.dur = 320;
    if (p >= 1) this.autoHeld = true;
    this.sync(p);
    if (this.track.classList && !this.track.classList.contains("mbz--scrolled")) {
      this.track.classList.add("mbz--scrolled");
    }
  };

  Engine.prototype.step = function (now) {
    this.now = now;
    if (this.auto && !this.autoDone && !reduce && !this.autoHold && this.visible()) this.autoAdvance(now);
    this.tick(); this.draw(now);
    if (this.running && !reduce) this.raf = requestAnimationFrame(this.step);
  };
  Engine.prototype.renderStatic = function () { this.heal = this.target = Math.max(this.target, this.scrub ? this.heal : 1); this.draw(0); this.sync(); };
  Engine.prototype.start = function () { if (this.running) return; this.running = true; this.raf = requestAnimationFrame(this.step); };
  Engine.prototype.stop = function () { this.running = false; cancelAnimationFrame(this.raf); };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", mountAll); else mountAll();
  window.addEventListener("load", mountAll);
  window.PhreeBalance = { mount: mountAll };
})();
